import * as React from "react";
import { splitEscaped } from "./split";

// Gantt-style timeline of experiment sweeps: one row per sweep, bars on a
// shared hour axis, with an optional note hanging off each bar end.
//
// Props match the MDX registry entry:
// `labels` comma-separated sweep names (blank segments stay positional; write
//          "\," for a literal comma inside a name)
// `starts`/`ends` hours from the first start; starts[i] < 0 = never started;
//          a start with a missing/negative/earlier end = running or unknown end
// `notes`  optional, semicolon-separated annotation per row (empty = none;
//          write "\;" for a literal semicolon)
// `unit`   optional axis unit suffix, default "h" (e.g. "min" when starts/ends
//          are minutes); only the axis and the spoken times change
//
// Rows are rendered for max(labels, starts) entries: a label without a start
// shows a "no data" marker, a start without a label is named "(unnamed)".
// When any bar is open-ended the axis gets a 15% tail so it has visible width.

export interface SweepTimelineProps {
  labels: string;
  starts: number[];
  ends: number[];
  notes?: string;
  unit?: string;
}

type Row =
  | { kind: "queued"; name: string; note: string }
  | { kind: "nodata"; name: string; note: string }
  | { kind: "open"; name: string; note: string; start: number }
  | { kind: "done"; name: string; note: string; start: number; end: number };

const fmt = (n: number) => Number(n.toFixed(2)).toString();

// Round tick step (1/2/5 x 10^k) giving at most ~5 intervals.
function niceStep(max: number): number {
  const raw = max / 5;
  const pow = Math.pow(10, Math.floor(Math.log10(raw)));
  for (const m of [1, 2, 5, 10]) if (m * pow >= raw - 1e-12) return m * pow;
  return 10 * pow;
}

const isNum = (v: unknown): v is number => typeof v === "number" && Number.isFinite(v);

export function SweepTimeline({ labels, starts, ends, notes, unit = "h" }: SweepTimelineProps) {
  const labelId = React.useId();
  const names = (labels ?? "") === "" ? [] : splitEscaped(labels ?? "", ",").map((s) => s.trim());
  const noteList = splitEscaped(notes ?? "", ";").map((s) => s.trim());
  const rowCount = Math.max(names.length, starts?.length ?? 0);

  const rows: Row[] = Array.from({ length: rowCount }, (_, i) => {
    const name = i < names.length ? names[i] || "—" : "(unnamed)";
    const note = noteList[i] ?? "";
    const s = starts?.[i];
    const e = ends?.[i];
    if (!isNum(s)) return { kind: "nodata", name, note };
    if (s < 0) return { kind: "queued", name, note };
    if (!isNum(e) || e < 0 || e < s) return { kind: "open", name, note, start: s };
    return { kind: "done", name, note, start: s, end: e };
  });

  const maxT = rows.reduce(
    (m, r) => (r.kind === "done" ? Math.max(m, r.end) : r.kind === "open" ? Math.max(m, r.start) : m),
    0,
  );
  const hasOpen = rows.some((r) => r.kind === "open");
  const raw = Math.max(maxT, 1) * (hasOpen ? 1.15 : 1);
  const step = niceStep(raw);
  const intervals = Math.max(1, Math.ceil(raw / step - 1e-9));
  const axisMax = intervals * step;
  const decimals = Math.max(0, -Math.floor(Math.log10(step) + 1e-9));
  const pct = (h: number) => (h / axisMax) * 100;
  const at = (n: number) => (unit === "h" ? `hour ${fmt(n)}` : `${fmt(n)} ${unit}`);

  return (
    <div className="gc-widget sw-widget">
      <div className="gc-widget-label" id={labelId}>
        <span aria-hidden="true">// </span>sweep timeline — {rows.length} sweeps, {fmt(axisMax)}{unit} axis
      </div>
      <ol className="sw-list" role="list" aria-labelledby={labelId}>
        {rows.map((r, i) => {
          const anchor = r.kind === "done" ? r.end : r.kind === "open" ? r.start : 0;
          const anchorPct = pct(anchor);
          const right = anchorPct > 55;
          const noteText = r.note ? `, note ${r.note}` : "";
          const sentence =
            r.kind === "queued"
              ? `${r.name}: queued, not run${noteText}`
              : r.kind === "nodata"
                ? `${r.name}: no data${noteText}`
                : r.kind === "open"
                ? `${r.name}: starts at ${at(r.start)}, running or end unknown${noteText}`
                : `${r.name}: starts at ${at(r.start)}, ends at ${at(r.end)}${noteText}`;
          return (
            <li key={i} className="sw-row" role="listitem">
              <span className="viz-sr">{sentence}</span>
              <span className="sw-name" aria-hidden="true">
                {r.name}
              </span>
              <div className="sw-track" aria-hidden="true">
                {r.kind === "queued" || r.kind === "nodata" ? (
                  <div className={"sw-queued" + (r.kind === "nodata" ? " nodata" : "")}>
                    {r.kind === "nodata" ? "no data" : "queued, not run"}
                  </div>
                ) : (
                  <>
                    <div className="sw-lane">
                      <div
                        className={"sw-bar" + (r.kind === "open" ? " open" : "")}
                        style={{
                          left: `${pct(r.start)}%`,
                          width: `${r.kind === "open" ? 100 - pct(r.start) : pct(r.end) - pct(r.start)}%`,
                        }}
                      />
                      {r.note && r.kind === "done" && <div className="sw-tick" style={{ left: `${anchorPct}%` }} />}
                    </div>
                    {r.note && (
                      <div
                        className="sw-note"
                        style={
                          right
                            ? { marginRight: `${100 - anchorPct}%`, textAlign: "right" }
                            : { marginLeft: `${anchorPct}%` }
                        }
                      >
                        {r.note}
                      </div>
                    )}
                  </>
                )}
              </div>
            </li>
          );
        })}
      </ol>
      <div className="sw-axis" aria-hidden="true">
        <div className="sw-axis-track">
          {Array.from({ length: intervals + 1 }, (_, k) => (
            <span
              key={k}
              className={"sw-axis-tick" + (k === intervals ? " last" : "")}
              style={{ left: `${pct(k * step)}%` }}
            >
              {(k * step).toFixed(decimals)}{unit}
            </span>
          ))}
        </div>
      </div>
      <div className="sw-key" aria-hidden="true">
        ▬ ran ▭ running or end unknown
      </div>
    </div>
  );
}

export default SweepTimeline;
